// A list and the detail of what is open in it, inside the panel (Cases, People,
// Agents).
//
// Promise:
// - Nothing open: the list alone fills the panel.
// - Something open: when the panel has room for both (SPLIT_MIN: the list's
//   least plus a document's width), the list sits on the left at the device's
//   list width and the detail takes the rest. The list gives way first as the
//   panel narrows (down to LIST_MIN), so the detail keeps a document's width.
//   The line between them is a handle (`Splitter`): drag to change the list's
//   width (remembered on the device), double-click for the default.
// - Below SPLIT_MIN only the detail is shown, and its header gets a way back
//   to the list (`BackToList`).
// - The person can fold the list away beside an open detail ("full width":
//   `f`, or the row `useWideMenuItem` gives the detail's menu) and bring it
//   back the same way. It stays folded while moving between items.
// - Clicking the open row again closes the detail: the list alone again
//   (`useReopenToList`; the old web's lists). A click with a modifier key is
//   a plain link, and Enter on the row still opens.
import { Link, useChildMatches } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  createContext,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDeviceSetting } from "../device/device-setting";
import {
  LIST_DEFAULT,
  LIST_MAX,
  LIST_MIN,
  listWidthSetting,
} from "../device/settings";
import { ICON_BUTTON } from "../ui/button";
import { Icon } from "../ui/icon";
import type { MenuItem } from "../ui/menu";
import { cornerStyle, useShellFrame } from "./frame";
import { actionsOf, chipsOf } from "./header-data";
import { DOC_WIDTH } from "./layout";
import { useBackTo } from "./list-memory";
import {
  type ShellHeaderInput,
  ShellUpContext,
  useShellEmbedded,
} from "./outside/outside";
import { OpenSidebarButton } from "./panel";
import { DockUp, PhoneTopDock } from "./phone";
import { useShortcuts } from "./shortcuts";
import { SideViewBack, SideViewControls, useSideView } from "./side-view";
import { Splitter } from "./splitter";
import { useTitleBarClear } from "./title-bar";

/** Below this panel width an open detail is shown alone. */
export const SPLIT_MIN = LIST_MIN + DOC_WIDTH;

/**
 * The list's width beside an open detail: the device's width, narrowed so the
 * detail keeps `detailMin` (never under LIST_MIN). `max` is how far the handle
 * may pull it now.
 */
export function listColumn(
  panel: number,
  pref: number,
  detailMin = DOC_WIDTH,
): { split: boolean; width: number; max: number } {
  const max = Math.max(LIST_MIN, Math.min(LIST_MAX, panel - detailMin));
  return {
    split: panel >= LIST_MIN + detailMin,
    width: Math.min(pref, max),
    max,
  };
}

interface ListDetailState {
  /** Inside a `ListDetail` (false for a list drawn elsewhere). */
  inside: boolean;
  /** The list is beside the open detail. */
  split: boolean;
  /** The list is shown alone (nothing open): it may lay out wide. */
  alone: boolean;
  /** Where the detail goes back to when the list is not shown. */
  listPath: string;
  /** The person folded the list away to give the detail the whole panel. */
  wide: boolean;
  /** Whether folding the list away (or back) means anything now. */
  canWide: boolean;
  toggleWide(): void;
}

const ListDetailContext = createContext<ListDetailState>({
  inside: false,
  split: true,
  alone: false,
  listPath: "/me",
  wide: false,
  canWide: false,
  toggleWide: () => {},
});

export const useListDetail = () => useContext(ListDetailContext);

/**
 * Whether the route below a list draws something of its own (one opened, or a
 * new one), not the list's empty index: what `ListDetail`'s `showing` takes
 * when a list has more than one kind of detail route.
 */
export function useShowsDetail(): boolean {
  return useChildMatches({
    select: (matches) => {
      const child = matches[0];
      return child !== undefined && !child.routeId.endsWith("/");
    },
  });
}

/**
 * What a click on a list's row does when that row is the open one: back to
 * the list alone (the detail closes). Returns true when it took the click
 * (the row then does nothing else). Only a plain click of the main button:
 * with Cmd / Ctrl / Shift / Alt it stays a link, and a click made by the
 * keyboard (Enter, `detail` 0) opens as before.
 */
export function useReopenToList(): (e: MouseEvent, open: boolean) => boolean {
  const { inside, listPath } = useListDetail();
  const backTo = useBackTo();
  return (e, open) => {
    if (!inside || !open || e.defaultPrevented) return false;
    if (!plainClick(e)) return false;
    e.preventDefault();
    backTo(listPath);
    return true;
  };
}

/** A click of the main button without a modifier key, made by a pointer. */
const plainClick = (e: MouseEvent) =>
  e.button === 0 &&
  e.detail !== 0 &&
  !e.metaKey &&
  !e.ctrlKey &&
  !e.shiftKey &&
  !e.altKey;

/**
 * The "full width" row of a detail's menu (folds the list away, or brings it
 * back), or null where there is no list beside to fold.
 */
export function useWideMenuItem(): MenuItem | null {
  const { canWide, wide, toggleWide } = useListDetail();
  const side = useSideView();
  if (!canWide || side !== null) return null;
  return {
    id: "wide",
    label: wide ? m.shell_wide_off() : m.shell_wide(),
    onSelect: toggleWide,
  };
}

export function ListDetail({
  list,
  detail,
  showing,
  listPath,
  listLabel,
  detailMin = DOC_WIDTH,
}: {
  list: ReactNode;
  detail: ReactNode;
  /** "list": nothing is open (the list fills the panel). */
  showing: "list" | "detail";
  listPath: string;
  /** Read aloud for the list column. */
  listLabel: string;
  /** The width the detail keeps beside the list (default: a document's). */
  detailMin?: number;
}) {
  const panel = useShellFrame().panelWidth;
  const [listPref, setListPref] = useDeviceSetting(listWidthSetting);
  // The width while the handle is held (stored on release).
  const [dragged, setDragged] = useState<number | null>(null);
  const col = listColumn(panel, dragged ?? listPref, detailMin);
  const open = showing === "detail";
  // Folded away by the person (`f` or the menu): a state of this screen, kept
  // while moving between its items, never in the URL.
  const [wide, setWide] = useState(false);
  const canWide = open && col.split;
  const beside = canWide && !wide;
  const toggleWide = useCallback(() => setWide((w) => !w), []);
  const state = useMemo(
    () => ({
      inside: true,
      split: beside,
      alone: !open,
      listPath,
      wide: canWide && wide,
      canWide,
      toggleWide,
    }),
    [beside, open, listPath, canWide, wide, toggleWide],
  );
  const canWideRef = useRef(canWide);
  canWideRef.current = canWide;
  useShortcuts([
    {
      id: "shell.wide",
      key: "f",
      group: "view",
      label: () => m.shell_wide_toggle(),
      when: () => canWideRef.current,
      run: toggleWide,
    },
  ]);
  return (
    <ListDetailContext.Provider value={state}>
      <div className="relative flex min-h-0 min-w-0 flex-1">
        {(!open || beside) && (
          <section
            aria-label={listLabel}
            className={`flex min-h-0 flex-col ${
              beside
                ? "flex-none border-r border-border-soft bg-list"
                : "min-w-0 flex-1"
            }`}
            style={beside ? { width: col.width } : undefined}
          >
            {list}
          </section>
        )}
        {open && (
          // The app's way back from a detail the window started on: its list.
          <ShellUpContext.Provider value={listPath}>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">{detail}</div>
          </ShellUpContext.Provider>
        )}
        {/* On a phone the Dock's left slot goes up to the list. */}
        {open && !beside && (
          <DockUp to={listPath} label={m.shell_back_to_list()} />
        )}
        {beside && (
          <Splitter
            label="list"
            at={col.width}
            width={col.width}
            min={LIST_MIN}
            max={col.max}
            resetTo={LIST_DEFAULT}
            onChange={setDragged}
            onCommit={(w) => {
              setDragged(null);
              setListPref(w);
            }}
            lineOffset={3}
          />
        )}
      </div>
    </ListDetailContext.Provider>
  );
}

/**
 * The detail's way back to the list, only when the list is not beside it.
 * Back over the entry when the list is the one before (the list comes back
 * where it was); a link to the list otherwise.
 */
export function BackToList() {
  const { split, listPath } = useListDetail();
  const backTo = useBackTo();
  if (split) return null;
  return (
    <Link
      to={listPath}
      aria-label={m.shell_back_to_list()}
      className={ICON_BUTTON}
      onClick={(e) => {
        // A key (Enter) goes the same way; a modifier key keeps the link.
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
          return;
        e.preventDefault();
        backTo(listPath);
      }}
    >
      <Icon kind="back" size={15} />
    </Link>
  );
}

/**
 * Below this width of its own row a detail header is narrow (a phone, a side
 * view): the title comes first, and what sits beside it folds (a label chip
 * keeps only its icon, a state's words and pill go into "…"). The old web's
 * header kept most of a long title on a phone; with the label, the state and
 * the buttons all at full size the title kept 3 letters.
 * The same width at which a side view's "open on its own screen" becomes an
 * icon.
 */
export const DETAIL_HEADER_NARROW = 560;

/** What sits beside the title, drawn for the room the header has. */
type ByRoom = ReactNode | ((narrow: boolean) => ReactNode);

const byRoom = (node: ByRoom, narrow: boolean) =>
  typeof node === "function" ? node(narrow) : node;

/** Whether this header's row is narrower than `DETAIL_HEADER_NARROW`. */
function useNarrowRow(ref: RefObject<HTMLElement | null>): boolean {
  const [narrow, setNarrow] = useState(false);
  // Read before paint, so a narrow row never shows its wide form first.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () =>
      setNarrow(el.clientWidth > 0 && el.clientWidth < DETAIL_HEADER_NARROW);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return narrow;
}

/**
 * The header row of a detail: the same height and line as `PanelHeader`. One
 * shape for every detail (Case, Person, document, chat, Topic): its title
 * (no breadcrumbs: the list or the sidebar already says where it is), then
 * what the detail puts beside it (`start`: its label chips), and its actions
 * (the star and "…" last) at the right end. When the list is not beside it,
 * the row also opens the sidebar (compact) and goes back to the list. In a
 * side view it ends with the side view's controls.
 *
 * The title has the first claim on the row: what is beside it gives way
 * first. `start` and `actions` may be functions of `narrow` (the row is
 * under `DETAIL_HEADER_NARROW`) to draw a folded form; a plain `start` is
 * left out when narrow.
 */
export function DetailHeader({
  header,
  title = header?.title,
  start = header?.chips?.length
    ? (narrow) => chipsOf(header, narrow)
    : undefined,
  actions = header ? (narrow) => actionsOf(header, narrow) : undefined,
  end,
}: {
  /**
   * The header as the outside's data (shell/outside): its title, chips, note
   * and buttons, drawn in its order, each by its own `render` when it has
   * one. The same data goes to a frame drawn outside (`useShellHeader`).
   */
  header?: ShellHeaderInput;
  /** The open thing's name, one line (cut with an ellipsis; 13.5px fg). */
  title?: ReactNode;
  start?: ByRoom;
  actions?: ByRoom;
  /** After the actions, the web's own (a peek's close): not in the data. */
  end?: ByRoom;
}) {
  const frame = useShellFrame();
  const { split } = useListDetail();
  // In a side view the screen around has the sidebar and the list.
  const side = useSideView() !== null;
  const row = useRef<HTMLElement>(null);
  const clear = useTitleBarClear(row);
  const narrow = useNarrowRow(row);
  const embedded = useShellEmbedded();
  // Embedded, the host draws the header from what the detail hands it
  // (`useShellHeader`); at a phone's width the phone's top Dock still keeps
  // its room (the host draws its buttons there). Wider (the iPad), the row is
  // kept unseen right above the screen, so what its buttons open (a
  // document's index, a Case's label) hangs from the top edge under the
  // host's bar, as a press there asks.
  if (embedded && !side && !frame.phone)
    return (
      <div className="relative h-0 flex-none">
        <div className="invisible absolute inset-x-0 bottom-full flex h-11 items-center justify-end gap-2 px-4 [&_[data-popover]]:visible">
          {typeof start === "function" && (
            // In the middle, as the host's bar shows the chips with the title
            // (centered without a transform: under one, the popover's fixed
            // place is taken from it, not from the screen).
            <div className="absolute inset-x-0 flex items-center justify-center gap-2">
              {start(false)}
            </div>
          )}
          {byRoom(actions, false)}
        </div>
      </div>
    );
  // A phone has a Dock at the top instead: the body has the title, the Dock
  // the folded forms of what is beside it and of the actions.
  if (frame.phone) {
    const folded = typeof start === "function" ? start(true) : null;
    return (
      <PhoneTopDock
        back={!side}
        hostBack={!side && header?.back === true}
        actions={
          folded != null || actions != null || end != null ? (
            <>
              {folded}
              {byRoom(actions, true)}
              {byRoom(end, true)}
            </>
          ) : undefined
        }
      />
    );
  }
  const beside =
    typeof start === "function" ? start(narrow) : narrow ? null : start;
  return (
    <header
      ref={row}
      // A container: what a detail puts in its actions can give way by the
      // row's own width (a side view is narrower than the window says).
      className="@container flex h-11 flex-none items-center gap-2 border-b border-border-soft px-4"
      // Alone in the panel it is at the corner; beside the list or in a side
      // view it is not.
      style={{
        ...(split || side ? undefined : cornerStyle(frame)),
        ...clear,
      }}
    >
      {!side && frame.compact && !split && <OpenSidebarButton />}
      {!side && <BackToList />}
      <SideViewBack />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/* Not a heading: the body has the thing's own (often editable) title. */}
        {title != null && title !== "" && (
          <div className="fade-end min-w-0 flex-initial text-[13.5px] text-fg">
            {title}
          </div>
        )}
        {beside != null && beside !== false && (
          // Gives way before the title does (shrinks many times faster), down
          // to its own least width (a folded chip is never cut).
          <div className="flex shrink-[50] items-center gap-2">{beside}</div>
        )}
      </div>
      {byRoom(actions, narrow)}
      {byRoom(end, narrow)}
      <SideViewControls />
    </header>
  );
}

export { HEADER_ICON_BUTTON, HEADER_PILL } from "./header-data";
